import {inject, Injectable} from '@angular/core';
import {
    getUserInstance,
    UserSearchCriteria,
    User,
    UserCreationResponse,
    Medic, daysInAYear
} from 'app/core/types/smm/smm-person.types';
import {
    BehaviorSubject,
    concat,
    forkJoin,
    from,
    lastValueFrom,
    mergeMap,
    Observable,
    of,
    switchMap, take,
    tap,
    throwError, toArray
} from 'rxjs';
import {UrlEnum} from "app/core/types/url-enum";
import {HttpClient, HttpErrorResponse, HttpHeaders} from "@angular/common/http";
import {UrlService} from "./url.service";
import {CognitoPermissions, RoleGroup} from "../types/permission.types";
import {PermissionsService} from "./permissions.service";
import {ImgService} from "./img.service";
import {SMMUserSelectionService} from "./smm.user.selection.service";
import {Patient} from "../../features/patient/patient.types";
import {DateTimeService} from "./date.time.service";

@Injectable({ providedIn: 'root' })
export class UserService {
    //services
    private _httpClient = inject(HttpClient);
    private _urlService : UrlService = inject(UrlService);
    private _permissionService : PermissionsService = inject(PermissionsService);
    private _imgService: ImgService = inject(ImgService);
    private _dtSrv: DateTimeService = inject(DateTimeService);
    private _userSelectionService: SMMUserSelectionService = inject(SMMUserSelectionService);

    //observables
    private _loggedInUser: BehaviorSubject<User | null> = new BehaviorSubject(getUserInstance());
    private _team: BehaviorSubject<User[] | null> = new BehaviorSubject(null);
    private _patients: BehaviorSubject<User[] | null> = new BehaviorSubject(null);
    private _busy: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private userCache:Map<string, User> = new Map();

    get busy$(){
        return this._busy.asObservable();
    }

    initLoggedInUser()  {
        if( this._loggedInUser?.value?.calendar != null &&
            this._loggedInUser?.value?.calendar?.length > 0 &&
            this._loggedInUser?.value?.medicInfo &&
            this._loggedInUser?.value?.medicInfo?.user
        ){
            // no need to update user again
            return;
        }
        const usr = this.loggedInUserFromLocalStorage;
        this.updateUserImg(usr).then(v=>{
            //update user
            this.fillInAllUserData(usr, true);
            this._loggedInUser.next(usr);
        });
    }

    //logged user
    get loggedInUser$(): Observable<User> {
        return this._loggedInUser.asObservable();
    }

    public getLoggedInUserCurrentValue(): User {
        return this._loggedInUser.value;
    }

    private get loggedInUserFromLocalStorage():User {
        const userJson = localStorage.getItem('loggedInUser');
        return JSON.parse(userJson) as User;
    }

    async updateLoggedInUser(email: string, name: string, id: any) {
        let usr:User;
        if(this._permissionService.isPlatformOwner()){
            //no need to update user if platform owner
            usr= getUserInstance();
            usr.email = email;
            usr.cognitoUsername=name;
            usr.id = id;
        }else{
            try{
                usr = await lastValueFrom(this._httpClient.get<User>(this._urlService.getUrl(UrlEnum.private_user).replace('{userId}', id)));
            }catch(err){
                //fallback .. rethink.. maybe sign - out
                usr= getUserInstance();
                usr.email = email;
                usr.cognitoUsername=name;
                usr.id = id;
            }
        }
        await this.updateUserImg(usr);
        // Store the value
        localStorage.setItem('loggedInUser', JSON.stringify(usr));
        // Update observable
        this._loggedInUser.next(usr);
    }

    updateLoggedInUserStatus(status: string) {
        this._loggedInUser.pipe(take(1)).subscribe( usr => {
            usr.status = status;
            this._loggedInUser.next(usr);
        })
    }

    private async updateUserImg(usr: User): Promise<User> {
        //update
        if (usr?.userProfilePath) {
            const img:Blob = await this._imgService.getAvatar(usr);
            if(img != null){
                this._imgService.setAvatarImgFromBlob(usr, img);
            }
        }
        return usr;
    }

    get team$(): Observable<User[]>{
        return this._team.asObservable();
    }

    // team related
    /** Used to initialize team observable */
    public getTeam(useCache:boolean) {
        if(this._permissionService.isPlatformOwner()){
            //platform owner does not have a team.. he's THE team :))
            return of([]);
        }

        if(useCache && this._team.value){
            return;
        }

        this._permissionService.getSecurityMaxGroups().pipe(mergeMap(roles => {
                const acceptedRoles: string[] = (roles.filter(role => role.name != 'role_patient').map(r => r.name));
                return this._httpClient.post<User[]>(this._urlService.getUrl(UrlEnum.private_search_user_in_role), {
                    "includedRoles": acceptedRoles,
                });
            }),
        ).subscribe(
            {
                next: (value) => {
                    if(this._permissionService.isTeamMember()){
                        //add logged in user into Team
                        // value.push(this._loggedInUser.value);
                    }
                    value.forEach(usr => {
                        this.fillInAllUserData(usr);
                    });
                    this._team.next(value)
                },
                error: (error) => {
                    this._team.next([])
                },
                complete: () => {}
            }
        )
    }

    get patients$(){
        return this._patients.asObservable();
    }

    public clearLastSearchResult(){
        return this._patients.next([]);
    }

    public findUsers(filter :UserSearchCriteria): Observable<User[]> {
        const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');
        this._busy.next(true);
        return this._httpClient.post<User[]>(this._urlService.getUrl(UrlEnum.private_search_user), JSON.stringify(filter),{headers: headers}).pipe(
            mergeMap(
                users => {
                            for (const user of users) {
                                this.fillInAllUserData(user);
                            }
                            return of(users);
                        }
            )
        ).pipe(tap(users => {
            this._patients.next(users);
            this._busy.next(false);
        }));
    }

    public getCalendarEventParticipants(eventId:string):Observable<User[]> {
        return this._httpClient.get<User[]>(this._urlService.getUrl(UrlEnum.users_in_calendar_evt).replace('{eventId}', eventId))
            .pipe(
                switchMap(users =>{
                    users.forEach(u => this.fillInAllUserData(u))
                    return of(users);
                })
            );
    }

    public getUser(id:string): Observable<User> {
        //check cache first
        if(this.userCache.has(id)){
            return of(this.userCache.get(id)).pipe(
                switchMap(user =>{
                    this.fillInAllUserData(user);
                    return this.updateUserImg(user);
                }),
            );
        }

        return this._httpClient.get<User>(this._urlService.getUrl(UrlEnum.private_user).replace('{userId}', id))
            .pipe(
                switchMap(user =>{
                    this.fillInAllUserData(user);
                    return this.updateUserImg(user);
                }),
            ).pipe(tap(user => {
                if(!this._patients.value){
                    //console.log("user updated in cache ... was empty");
                    this._patients.next([user]);
                }
                else{
                    this._patients.pipe(take(1)).subscribe( usersVal => {
                        if(usersVal) {
                            const index: number = usersVal.findIndex(usr => usr.id == user.id);
                            if (index >= 0) {
                                //console.log("user updated in cache");
                                usersVal[index] = user;
                            } else {
                                //console.log("user pushed to cache");
                                usersVal.push(user);
                            }
                            //aici cred ca e bug-ul
                            this._patients.next(usersVal);
                        }
                    });
                }
            }));
    }

    public deleteUser(user:User) {
        return this._httpClient.delete<any>(this._urlService.getUrl(UrlEnum.private_user).replace('{userId}', user.id))
            .pipe(tap(value => {
                this._patients.pipe(take(1)).subscribe( usersVal => {
                    if(usersVal) {
                        usersVal.splice(usersVal.findIndex( userT => userT.id === user.id), 1);
                        this._team.next([].concat(usersVal));
                    }
                })

                this._team.pipe(take(1)).subscribe( teamVal => {
                    if(teamVal) {
                        teamVal.splice(teamVal.findIndex( userT => userT.id === user.id), 1);
                        this._team.next([].concat(teamVal));
                    }
                })

                this.userCache.delete(user.id);
            }))
    }

    public updateUser(person:User, rolesToBeAdded:RoleGroup[], rolesToBeRemoved:RoleGroup[]): Observable<string[]>{
        return this.updateUserBasicData(person)
            .pipe(
                switchMap(userID => {
                    // Execute all addRole calls in sequence
                    const addRolesSequentially$ = rolesToBeAdded.length > 0
                        ? from(rolesToBeAdded).pipe(
                            mergeMap(role => this.addRole(userID, role), 1)
                          )
                        : of(null);

                    // Execute all removeRole calls in sequence after adds complete
                    const removeRolesSequentially$ = rolesToBeRemoved.length > 0
                        ? from(rolesToBeRemoved).pipe(
                            mergeMap(role => this.removeRole(userID, role), 1)
                          )
                        : of(null);

                    // Execute adds first, then removes, and collect all results
                    return concat(addRolesSequentially$, removeRolesSequentially$).pipe(
                        toArray(),
                        switchMap(results => of(results.filter(r => r !== null)))
                    );
                }),
            );
    }

    private updateUserBasicData(user:User) {
        const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');
        return this._httpClient.put<string>(
            this._urlService.getUrl(UrlEnum.private_user).replace('{userId}', user.id),
            JSON.stringify(user),
     {headers: headers}).pipe(tap(
         result => {
             // Update user cache
             if(this.userCache.has(user.id)) {
                 const cachedUser = this.userCache.get(user.id);
                 if (cachedUser) {
                     Object.keys(user).forEach(key => {
                         if (user[key] != null) {
                             cachedUser[key] = user[key];
                         }
                     });
                     this.userCache.set(user.id, cachedUser);
                 }
             }

             // Update user in team
             this._team.pipe(take(1)).subscribe( teamVal => {
                 if(teamVal) {
                     const index: number = teamVal.findIndex(usr => usr.id == user.id);
                     if (index >= 0) {
                         const teamUser = teamVal[index];
                         Object.keys(user).forEach(key => {
                             if (user[key] != null) {
                                 teamUser[key] = user[key];
                             }
                         });
                         teamVal[index] = teamUser;
                         this._team.next(teamVal);
                     }
                 }
             })
         }
        ));
    }

    public createNewUser(person:User, neededRoles:RoleGroup[]): Observable<string[]>{
        if(!neededRoles || neededRoles.length < 1){
            return throwError(()=> new Error("At least one role is needed."));
        }
        return this.createUserOnlyWithInitialRole(person, neededRoles[0])
            .pipe(
                switchMap(response => {
                    person.id = response.userId;
                    person.calendar = response.calendarId;
                    this.userCache.set(person.id, person);
                    return forkJoin(neededRoles.map(role => this.addRole(response.userId, role)));
            })
        ).pipe(
            tap( userIDs => {
                if(neededRoles.filter(role => role.name != 'role_patient')){
                    this._team.pipe(take(1)).subscribe( teamVal => {
                        teamVal.push(person);
                        this._team.next(teamVal);
                    })
                }
            })
        );
    }

    private createUserOnlyWithInitialRole(person:User, role:RoleGroup):Observable<UserCreationResponse> {
        //TODO- refactor backend so this case is nopt needed
        let creationUrl:string;
        switch (role.name){
            case "role_patient": creationUrl=this._urlService.getUrl(UrlEnum.private_create_patient); break;
            case "role_doctor": creationUrl=this._urlService.getUrl(UrlEnum.private_create_doctor); break;
            case "role_tenant_admin": creationUrl=this._urlService.getUrl(UrlEnum.private_create_admin); break;
            case "role_front_desk": creationUrl=this._urlService.getUrl(UrlEnum.private_create_front_desk); break;
            case "role_visitor": creationUrl=this._urlService.getUrl(UrlEnum.private_create_visitor); break;
            default: return throwError(()=> new Error("Role provided is invalid. Must be patient, doctor or admin"));
        }

        const jsonUser = JSON.stringify(person);
        const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');
        return this._httpClient.post<UserCreationResponse>(creationUrl, jsonUser, {headers: headers});
    }

    private addRole(userId: string, role: RoleGroup) {
        return this._httpClient.post<string>(this._urlService.getUrl(UrlEnum.private_user_role).replace('{userId}', userId).replace('{roleId}',role.id), {});
    }

    private removeRole(userId: string, role: RoleGroup) {
        return this._httpClient.delete<string>(this._urlService.getUrl(UrlEnum.private_user_role).replace('{userId}', userId).replace('{roleId}',role.id));
    }

    private getPatientInfo(userID: string): Observable<Patient> {
        return this._httpClient.get<Patient>(this._urlService.getUrl(UrlEnum.private_get_patient_from_user)
            .replace("{userId}", userID)
        );
    }

    private getMedicInfo(userID: string): Observable<Medic> {
        return this._httpClient.get<Medic>(this._urlService.getUrl(UrlEnum.private_get_doctor_from_user)
            .replace("{userId}", userID)
        );
    }

    public fillInAllUserData(user: User, updateAsSelectedMedic?: boolean, updateAsSelectedUser?: boolean) {
        //update patient info8
        if(this._permissionService.userHasAnyOfRoles(user.userRole, [CognitoPermissions.PATIENT])) {
            if(!user.patientInfo) {
                this.getPatientInfo(user.id).subscribe(patientInfo => {
                    user.patientInfo = patientInfo;
                    //update user img
                    this.updateUserImg(user).then(value => {
                        if(updateAsSelectedUser){
                            this._userSelectionService.updateSelectedUser(user);
                        }
                    });
                });
            }
        }else{
            user.patientInfo = null;
        }

        //update medic info
        if(this._permissionService.userHasAnyOfRoles(user.userRole, [CognitoPermissions.DOCTOR])) {
            if(!user.medicInfo) {
                //if user is a medic, get medic details
                this.getMedicInfo(user.id).subscribe(medicInfo => {
                    user.medicInfo = medicInfo;
                    //update user img
                    this.updateUserImg(user).then(value => {
                        if(updateAsSelectedMedic) {
                            this._userSelectionService.updateSelectedMedic(user);
                        }
                    });
                });
            }
        }else{
            user.medicInfo = null;
        }

        //update user img
        if(!user.avatar){
            this.updateUserImg(user);
        }

        this.userCache.set(user.id, user);
    }

    public selectCurrentlyLoggedInUserAsSelectedMedic():boolean{
        if(this._permissionService.isMedic()){
            this._userSelectionService.updateSelectedMedic(this._loggedInUser.value);
            return true;
        }
        return false;
    }

    public getUserAge(usr:User){

        let usrDate = new Date();
        if(usr.dateOfBirth instanceof Array){
            usrDate = this._dtSrv.getDateAndTimeFromBEInDateObject(usr.dateOfBirth);
        }
        if(usr.dateOfBirth instanceof Date){
            usrDate = usr.dateOfBirth;
        }

        const time = Math.abs(usrDate.getTime() - new Date().getTime());
        const daysTotal = Math.floor((time/(1000 * 3600 * 24)));
        const yearsSince = Math.floor(daysTotal/daysInAYear);
        const daysSince = Math.floor( daysTotal - (yearsSince * daysInAYear));
        const daysUntillBirthday = 365-daysSince
        return {years:yearsSince, days: daysSince, daysUntillBirthDay: daysUntillBirthday };
    }
}
